import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Save,
  User,
  Briefcase,
  DollarSign,
  Calendar,
  FileText,
  Receipt,
  AlertCircle
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';

const RevenueFormPage = () => {
  const navigate = useNavigate();
  const { revenueId } = useParams();
  const isEditMode = Boolean(revenueId);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client: '',
    clientType: 'individual',
    service: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    source: 'invoice',
    invoiceId: '',
    caseId: '',
    status: 'completed',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Load revenue data if editing
  useEffect(() => {
    if (isEditMode) {
      // Mock data - سيتم استبداله بـ API
      setTimeout(() => {
        const mockRevenue = {
          client: 'محمد أحمد',
          clientType: 'individual',
          service: 'استشارة قانونية',
          amount: '5000',
          date: '2024-12-01',
          source: 'invoice',
          invoiceId: 'INV-2024-001',
          caseId: 'WZ-2024-001',
          status: 'completed',
          notes: 'إيراد من استشارة قانونية'
        };
        setFormData(mockRevenue);
      }, 300);
    }
  }, [isEditMode, revenueId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.client.trim()) {
      newErrors.client = 'اسم العميل مطلوب';
    }

    if (!formData.service.trim()) {
      newErrors.service = 'نوع الخدمة مطلوب';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'المبلغ مطلوب ويجب أن يكون أكبر من صفر';
    }

    if (!formData.date) {
      newErrors.date = 'تاريخ الإيراد مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Mock API call
    setTimeout(() => {
      console.log('Form Data:', formData);
      alert(isEditMode ? 'تم تحديث الإيراد بنجاح' : 'تم إضافة الإيراد بنجاح');
      navigate('/admin/finance/revenues');
      setLoading(false);
    }, 1000);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/revenues')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للإيرادات</span>
          </button>

          <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">
            {isEditMode ? 'تعديل إيراد' : 'إضافة إيراد جديد'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'تحديث معلومات الإيراد' : 'أدخل بيانات الإيراد الجديد'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#1E3A8A]" />
              بيانات العميل
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  اسم العميل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="client"
                  value={formData.client}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    errors.client
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="مثال: محمد أحمد"
                />
                {errors.client && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.client}
                  </p>
                )}
              </div>

              {/* Client Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  نوع العميل
                </label>
                <select
                  name="clientType"
                  value={formData.clientType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="individual">فرد</option>
                  <option value="company">شركة</option>
                </select>
              </div>

              {/* Case ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم القضية (اختياري)
                </label>
                <input
                  type="text"
                  name="caseId"
                  value={formData.caseId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="WZ-2024-001"
                />
              </div>
            </div>
          </div>

          {/* Revenue Details */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#1E3A8A]" />
              تفاصيل الإيراد
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Service */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  نوع الخدمة <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.service
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                    placeholder="مثال: استشارة قانونية، تمثيل قضائي..."
                  />
                </div>
                {errors.service && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.service}
                  </p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  المبلغ (ر.س) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.amount
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.amount}
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  تاريخ الإيراد <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.date
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                  />
                </div>
                {errors.date && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.date}
                  </p>
                )}
              </div>

              {/* Source */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  مصدر الإيراد
                </label>
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="invoice">فاتورة</option>
                  <option value="payment">دفعة</option>
                  <option value="contract">عقد</option>
                </select>
              </div>

              {/* Invoice ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم الفاتورة (اختياري)
                </label>
                <div className="relative">
                  <FileText className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="invoiceId"
                    value={formData.invoiceId}
                    onChange={handleChange}
                    className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    placeholder="INV-2024-001"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الحالة
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="completed">مكتمل</option>
                  <option value="pending">قيد المعالجة</option>
                  <option value="expected">متوقع</option>
                  <option value="cancelled">ملغي</option>
                </select>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  ملاحظات (اختياري)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  rows="3"
                  placeholder="أي ملاحظات إضافية..."
                ></textarea>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>جاري الحفظ...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{isEditMode ? 'حفظ التعديلات' : 'إضافة الإيراد'}</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/finance/revenues')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default RevenueFormPage;
